import { getConfigValue } from "../config/appConfig";

/**
 * Build security headers for browser → admin-backend API calls.
 * Adds a shared client API key + request hash before the request is sent.
 *
 * Headers:
 *   X-Client-Api-Key  — shared secret
 *   X-Client-Timestamp — ms epoch
 *   X-Client-Hash — SHA-256(timestamp + ":" + apiKey) hex
 *
 * Key must come from Secrets Manager / .env (REACT_APP_CLIENT_API_KEY). No code fallback.
 */

function _toHex(buffer) {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function _sha256Hex(text) {
  const data = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return _toHex(digest);
}

export function getClientApiKey() {
  return (getConfigValue("REACT_APP_CLIENT_API_KEY", "") || "").trim();
}

/**
 * @returns {Promise<Record<string, string>>}
 */
export async function buildClientSecurityHeaders() {
  const apiKey = getClientApiKey();
  if (!apiKey) {
    throw new Error(
      "REACT_APP_CLIENT_API_KEY is missing. Add REACT_APP_CLIENT_API_KEY to the FRONTEND AWS secret " +
        "(FRONTEND_AWS_SECRET_ID) or .env, then redeploy."
    );
  }

  const timestamp = String(Date.now());
  const hash = await _sha256Hex(`${timestamp}:${apiKey}`);

  return {
    "X-Client-Api-Key": apiKey,
    "X-Client-Timestamp": timestamp,
    "X-Client-Hash": hash,
  };
}
